import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { flatMap, take, tap } from 'rxjs/operators';
import { Observable } from 'rxjs/internal/Observable';
import { concat } from 'rxjs/internal/observable/concat';
import { forEach } from 'lodash';
import { v4 } from 'uuid';
import {
  AppActionsEnum,
  BrowserManagerActionsEnum,
  IKeyValue,
  RemoveCookies,
  setBrowserData,
  SetBrowserData,
  setCookie,
  SetCookie,
  SetCookies,
  setDeviceId,
  SetDeviceId,
} from '../actions';
import * as fromBuilder from './browser-manager.builder';
import { I2G_DEVICE_ID } from '../browser-manager.data';
import { IBrowserInfo } from '../reducers';
import { CookieService, WindowRefService } from 'i2g-ng-utils';

const userAgentParser = require('ua-parser-js');

@Injectable()
export class BrowserManagerEffects {
  constructor(
    private _actions: Actions,
    private _cookieService: CookieService,
    private _windowRefService: WindowRefService
  ) {}

  @Effect()
  initApp: Observable<
    SetCookie | SetBrowserData | SetDeviceId
  > = this._actions.pipe(
    ofType(AppActionsEnum.INIT_APP),
    take(1),
    flatMap(() => {
      const window = this._windowRefService.nativeWindow;
      const navigator = window.navigator;
      const parsedUserAgent = userAgentParser(navigator.userAgent);
      const deviceId = this._cookieService.getCookie(I2G_DEVICE_ID) || v4();
      const browserData: IBrowserInfo = fromBuilder.getBrowserData(
        parsedUserAgent,
        navigator,
        deviceId
      );

      return concat([
        setCookie(I2G_DEVICE_ID, deviceId),
        setDeviceId(deviceId),
        setBrowserData(browserData),
      ]);
    })
  );

  @Effect({ dispatch: false })
  setCookie: Observable<any> = this._actions.pipe(
    ofType(BrowserManagerActionsEnum.SET_COOKIE),
    tap(({ payload }: SetCookie) =>
      this._cookieService.setCookie(payload.key, payload.value)
    )
  );

  @Effect({ dispatch: false })
  setCookies: Observable<any> = this._actions.pipe(
    ofType(BrowserManagerActionsEnum.SET_COOKIES),
    tap(({ payload }: SetCookies) => {
      const { cookies } = payload;

      forEach(cookies, (cookie: IKeyValue) =>
        this._cookieService.setCookie(cookie.key, cookie.value)
      );
    })
  );

  @Effect({ dispatch: false })
  removeCookies: Observable<any> = this._actions.pipe(
    ofType(BrowserManagerActionsEnum.REMOVE_COOKIES),
    tap(({ payload }: RemoveCookies) =>
      payload.keys.forEach((key: string) =>
        this._cookieService.removeCookie(key)
      )
    )
  );
}
